/**
 * @file incomingCallsConstants.js
 * @description Constants for the Incoming Calls module within the Agent section.
 *              Covers UI strings, search filters, validation, error/success messages,
 *              call/button states, animation & polling config, local storage keys,
 *              ACS call config, tab config, console messages, regex patterns,
 *              UI dimensions, microphone permissions, and HTTP status codes.
 *
 * @module incomingCallsConstants
 *
 * @exports INCOMING_CALLS_STRINGS - UI label strings for the call panel, tabs & customer search.
 * @exports SEARCH_FILTER_OPTIONS  - Search filter field keys (lastName, phoneNumber, email).
 * @exports SEARCH_FILTER_LABELS   - Human-readable labels mapped from SEARCH_FILTER_OPTIONS.
 * @exports VALIDATION_MESSAGES    - Input validation error messages for the search form.
 * @exports INCOMING_CALLS_ERRORS  - Error strings for search, call control, polling & devices.
 * @exports CALL_STATES            - Enum of ACS call state strings (Idle, Connecting, etc.).
 * @exports BUTTON_STATES          - Enum of UI button states (enabled, disabled, loading).
 * @exports ANIMATION_CONFIG       - Blink animation timing constants.
 * @exports POLLING_CONFIG         - Job-offer polling interval configuration.
 * @exports STORAGE_KEYS           - localStorage key names for ACS identity & worker ID.
 * @exports CALL_CONFIG            - Default ACS call options (display name, audio, hangup).
 * @exports TAB_CONFIG             - Default tab and tab list config for the call panel.
 * @exports SUCCESS_MESSAGES       - Success/info strings for call lifecycle events.
 * @exports CONSOLE_MESSAGES       - Console log message strings for debugging.
 * @exports VALIDATION_PATTERNS    - Regex patterns for lastName, phoneNumber & email validation.
 * @exports UI_CONFIG              - UI dimension and styling constants (widths, padding, etc.).
 * @exports PERMISSION_TYPES       - Browser permission type keys (microphone).
 * @exports PERMISSION_STATES      - Browser permission state strings (granted, denied, prompt).
 * @exports HTTP_STATUS            - HTTP status code constants used in API response handling.
 */

// UI Strings and Labels
export const INCOMING_CALLS_STRINGS = {
  // Search Customer
  SEARCH_CUSTOMER: "Search Customer",
  SEARCH_CUSTOMER_TITLE: "Search Customer",
  CUSTOMER_NOT_FOUND: "No customer information was found matching this phone number. Please search for the customer and attach the profile to this call.",
  ENTER_LAST_NAME: "Enter last name",
  ENTER_PHONE_NUMBER: "Enter phone number",
  ENTER_EMAIL_ADDRESS: "Enter email address",
  ENTER_SEARCH_TEXT: "Enter search text",
  SEARCHING: "Searching...",
  CANCEL: "Cancel",
  SEARCH: "Search",

  // Add Customer Related Records
  ADD: "Add",
  ADDING: "Adding...",
  ADD_RELATED_RECORDS_TITLE: "Add Related Records",
  
  // Call Panel
  CALL_PANEL: "Call Panel",
  ACCEPT_CALL: "Accept Call",
  START_CALL: "Start Call",
  DISCONNECT_HANGUP: "Disconnect / Hangup",
  COMPLETE_ASSIGNMENT: "Complete Assignment",
  START_TRANSCRIBING: "Start Transcribing",
  STOP_TRANSCRIBING: "Stop Transcribing",
  TRANSFER: "Transfer",
  MUTE: "🔊 Mute",
  UNMUTE: "🔇 Unmute",
  
  // Call Status
  CALL_STATUS: "📶 Call Status:",
  DURATION: "⏱ Duration:",
  LOADING: "Loading...",
  
  // Job Offer Information
  INCOMING_JOB_OFFER: "Incoming Job Offer",
  ACCEPTED_OFFER: "Accepted Offer",
  JOB_ID: "Job ID:",
  OFFER_ID: "Offer ID:",
  CONNECTION_ID: "Connection ID:",
  CUSTOMER_PHONE_NUMBER: "Customer Phone Number:",
  CUSTOMER_GUID: "Customer GUID:",
  WORKER_ID: "Worker ID:",
  AGENT_ACS_ID: "Agent ACS ID:",
  ASSIGNMENT_ID: "Assignment ID:",
  
  // Tab Labels
  CONVERSATION: "Conversation",
  PROFILE: "Profile",
  DATASET: "Dataset",
  NOTES: "Notes",
  PAYMENT: "Payments",
  HISTORY: "History",
  CALLS: "Calls",
  CHAT: "Chats",
  TEXTS: "Texts",
  EMAILS: "Emails",
  
  // Customer Information
  CUSTOMER_INFORMATION: "Customer Information",
  CONVERSATION_DESCRIPTION: "Recording and transcript are enabled by default. They are available for review after the call ends",
};

// Search Filter Options
export const SEARCH_FILTER_OPTIONS = {
  LAST_NAME: "lastName",
  PHONE_NUMBER: "phoneNumber",
  EMAIL: "email",
};

// Search Filter Labels
export const SEARCH_FILTER_LABELS = {
  [SEARCH_FILTER_OPTIONS.LAST_NAME]: "Last Name",
  [SEARCH_FILTER_OPTIONS.PHONE_NUMBER]: "Phone Number",
  [SEARCH_FILTER_OPTIONS.EMAIL]: "Email",
};

// Validation Messages
export const VALIDATION_MESSAGES = {
  SEARCH_TEXT_REQUIRED: "Search text is required",
  SELECT_FILTER: "Please select a search filter",
  INVALID_LAST_NAME: "Last name should contain only letters and spaces",
  INVALID_PHONE_NUMBER: "Please enter a valid phone number",
  INVALID_EMAIL: "Please enter a valid email address",
  INVALID_FILTER: "Invalid filter selection",
};

// Error Messages
export const INCOMING_CALLS_ERRORS = {
  // Search Errors
  SEARCH_FAILED: "Search failed. Please try again.",
  
  // Call Control Errors
  ACCEPT_OFFER_FAILED: "❌ Accept offer failed:",
  MISSING_ACS_IDS: "❌ Missing customer or agent ACS ID",
  BACKEND_CALL_FAILED: "❌ Failed backend call:",
  INITIATE_CALL_ERROR: "❌ Error initiating call:",
  HANGUP_FAILED: "❌ Hangup failed:",
  COMPLETE_ASSIGNMENT_FAILED: "❌ Complete assignment failed:",
  MUTE_TOGGLE_ERROR: "Mute toggle error:",
  
  // Polling Errors
  POLLING_ERROR: "❌ Error polling job offers:",
  
  // Device Errors
  NO_MICROPHONES: "⚠️ No microphones found!",
  FAILED_ACCEPT_CALL: "❌ Failed to accept incoming call",
  
  // General Errors
  LOADING_ERROR: "Error loading call data",
  CONNECTION_ERROR: "Connection error",
  PERMISSION_ERROR: "Microphone permission denied",
};

// Call States
export const CALL_STATES = {
  IDLE: "Idle",
  CONNECTING: "Connecting",
  CONNECTED: "Connected",
  DISCONNECTED: "Disconnected",
  RINGING: "Ringing",
  INCOMING: "Incoming",
};

// Button States
export const BUTTON_STATES = {
  ENABLED: "enabled",
  DISABLED: "disabled",
  LOADING: "loading",
};

// Animation Constants
export const ANIMATION_CONFIG = {
  BLINK_INTERVAL: 500,
  BLINK_DURATION: "1s",
  BLINK_TIMING: "linear",
  BLINK_ITERATION: "infinite",
};

// Polling Configuration
export const POLLING_CONFIG = {
  DEFAULT_INTERVAL: 5000,
  BLINK_INTERVAL: 500,
};

// Local Storage Keys
export const STORAGE_KEYS = {
  ACS_IDENTITY_TOKEN: "ACSIdentityToken",
  WORKER_ID: "workerid",
  ACS_IDENTITY: "acsIdentity",
};

// Call Configuration
export const CALL_CONFIG = {
  DISPLAY_NAME: "Agent",
  AUDIO_OPTIONS: {
    MUTED: false,
  },
  HANGUP_OPTIONS: {
    FOR_EVERYONE: false,
  },
};

// Tab Configuration
export const TAB_CONFIG = {
  DEFAULT_TAB: "Profile",
  TABS: [
    { label: "Conversation", value: "Conversation" },
    { label: "Profile", value: "Profile" },
    { label: "Dataset", value: "Dataset" },
    { label: "Notes", value: "Notes" },
    { label: "Payment", value: "Payment" },
    { label: "History", value: "History" },
    { label: "Calls", value: "Calls" },
    { label: "Chat", value: "Chat" },
    { label: "Texts", value: "Texts" },
    { label: "Emails", value: "Emails" },
  ],
};

// Success Messages
export const SUCCESS_MESSAGES = {
  OFFER_ACCEPTED: "✅ Agent accepted transferred call",
  BACKEND_CONNECTED: "✅ Backend connected agent to call:",
  CALL_HUNG_UP: "📴 Call hung up",
  CALL_AGENT_DISPOSED: "🧹 Call agent disposed",
  ASSIGNMENT_COMPLETED: "✅ Assignment completed",
  CALL_STARTED: "📞 Start listening...",
  TRANSFER_INITIATED: "📞 Initiating transfer via backend...",
};

// Console Log Messages
export const CONSOLE_MESSAGES = {
  WORKER_ID_LOG: "worker id is: ",
  ASSIGNMENT_ID_LOG: "📌 Assignment ID:",
  CALL_STATE_CHANGED: "📶 Call state changed:",
  CALL_AGENT_CREATED: "Call agent created successfully",
  CALL_INITIATED: "Call initiated successfully",
};

// Regex Patterns
export const VALIDATION_PATTERNS = {
  LAST_NAME: /^[A-Za-z\s]+$/,
  PHONE_NUMBER: /^[\+]?[1-9][\d]{0,15}$/,
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

// UI Dimensions and Styling
export const UI_CONFIG = {
  BUTTON_WIDTH: 170,
  MODAL_WIDTH: 400,
  MODAL_MAX_WIDTH: 800,
  MODAL_MARGIN_TOP: 8,
  MODAL_PADDING: 3,
  MODAL_BORDER_RADIUS: 2,
  GAP: 2,
  MARGIN_BOTTOM: 2,
  MARGIN_TOP: 2,
  PADDING: 4,
  FONT_SIZE_SMALL: '14px',
  PADDING_BOTTOM: 2,
};

// Permission Types
export const PERMISSION_TYPES = {
  MICROPHONE: "microphone",
};

// Permission States
export const PERMISSION_STATES = {
  GRANTED: "granted",
  DENIED: "denied",
  PROMPT: "prompt",
};

// HTTP Status Codes
export const HTTP_STATUS = {
  CREATED: 201,
  OK: 200,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};
